import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { useCaregiverPortal } from '../context/CaregiverPortalContext';
import {
  CaregiverFormData,
  INITIAL_FORM_DATA,
  RETIRED_PORTAL_STATUS,
  STEP_PORTAL_STATUS,
} from '../pages/CaregiverForm/types';
import { validateStep, FieldErrors } from '../pages/CaregiverForm/validation';
import { SIGNATURE_STEP, TOTAL_STEPS } from '../pages/CaregiverForm/constants';
import { getPicklistValues } from '../config/picklistConfig';

/**
 * Convert a Nexus API value (e.g. "CHINESE") back to the portal's display
 * label (e.g. "Chinese") by matching against the picklist config.
 */
function apiValueToPortalLabel(fieldName: string, apiValue: string): string {
  const portalOptions = getPicklistValues('caregiver', fieldName);
  if (!portalOptions.length) return apiValue;

  if (portalOptions.includes(apiValue)) return apiValue;

  const normalized = apiValue.toLowerCase().replace(/_/g, ' ');
  const match = portalOptions.find(
    (opt) => opt.toLowerCase().replace(/_/g, ' ') === normalized,
  );
  return match ?? apiValue;
}

/**
 * Normalize a Nexus record value into the string/boolean type the form expects.
 * - Arrays (MULTIPICKLIST) → semicolon-separated portal labels
 * - Uppercase API strings (PICKLIST) → portal display labels
 * - Numbers → strings
 * - Booleans stay booleans
 */
function normalizeNexusValue(key: string, value: unknown, formDefault: unknown): string | boolean {
  if (value === null || value === undefined) return formDefault as string | boolean;
  if (typeof formDefault === 'boolean') return value === true || value === 'true';

  if (Array.isArray(value)) {
    return value.map((v) => apiValueToPortalLabel(key, String(v))).join(';');
  }

  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return apiValueToPortalLabel(key, value);
  return value as string;
}

export function useCaregiverForm() {
  const navigate = useNavigate();
  const { accessToken, refreshAccessToken, user, logout } = useAuth();
  const { refresh: refreshPortalState } = useCaregiverPortal();

  const handleCaregiverResolutionResponse = useCallback(async (response: Response): Promise<boolean> => {
    if (response.status !== 409) return false;

    const body = await response.clone().json().catch(() => null);
    if (
      body?.code !== 'CAREGIVER_REGISTRATION_REQUIRED'
      && body?.code !== 'CAREGIVER_LINK_CONFLICT'
    ) return false;

    const mobile = user?.username?.replace(/\D/g, '') || '';
    await logout();
    const params = new URLSearchParams({
      [body.code === 'CAREGIVER_REGISTRATION_REQUIRED'
        ? 'registrationRequired'
        : 'caregiverLinkConflict']: 'true',
    });
    if (mobile) params.set('mobile', mobile);
    navigate(`/start?${params.toString()}`, { replace: true });
    return true;
  }, [logout, navigate, user?.username]);

  // Fetch wrapper that auto-refreshes token on 401
  const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers = { ...options.headers, Authorization: `Bearer ${accessToken}` } as Record<string, string>;
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      const refreshedToken = await refreshAccessToken();
      if (refreshedToken) {
        const retryHeaders = {
          ...options.headers,
          Authorization: `Bearer ${refreshedToken}`,
        } as Record<string, string>;
        return fetch(url, { ...options, headers: retryHeaders });
      }
    }
    return response;
  };

  const [formData, setFormData] = useState<CaregiverFormData>({ ...INITIAL_FORM_DATA });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isGeneratingDocs, setIsGeneratingDocs] = useState(false);
  const [generatedDocs, setGeneratedDocs] = useState<Array<{ id: number; documentName: string; createdAt: string }>>([]);
  const [docGenError, setDocGenError] = useState<string | null>(null);

  // Load caregiver data from Nexus on mount
  useEffect(() => {
    loadCaregiverData();
  }, []);

  const loadCaregiverData = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch('/api/caregiver');
      if (response.ok) {
        const { caregiver } = await response.json();
        const mapped: Partial<CaregiverFormData> = {};
        for (const key of Object.keys(INITIAL_FORM_DATA)) {
          if (caregiver[key] !== undefined && caregiver[key] !== null) {
            (mapped as any)[key] = normalizeNexusValue(
              key,
              caregiver[key],
              (INITIAL_FORM_DATA as any)[key],
            );
          }
        }
        // Derive county checkboxes from areas of work
        if (mapped.areasOfWorkMiamiDade) (mapped as any).countyMiamiDade = true;
        if (mapped.areasOfWorkBroward) (mapped as any).countyBroward = true;
        if (mapped.areasOfWorkPalmBeach) (mapped as any).countyPalmBeach = true;

        setFormData((prev) => ({ ...prev, ...mapped }));

        // Determine current step from portalStatus (use normalized value, not raw Nexus)
        const rawStatus = mapped.portalStatus;
        const normalizedStatus = rawStatus
          ? RETIRED_PORTAL_STATUS[rawStatus.toUpperCase().replace(/\s+/g, '_')] ?? rawStatus
          : rawStatus;
        if (normalizedStatus) {
          const stepIndex = STEP_PORTAL_STATUS.indexOf(normalizedStatus);
          if (stepIndex >= 0) {
            if (normalizedStatus === 'Submitted') {
              setIsSubmitted(true);
              setCurrentStep(TOTAL_STEPS);
              // Load existing documents
              authFetch('/api/documents/my?initial=true').then(r => r.ok ? r.json() : []).then(setGeneratedDocs).catch(() => {});
            } else {
              setCurrentStep(stepIndex + 1);
            }
          }
        }
      } else {
        await handleCaregiverResolutionResponse(response);
      }
    } catch (error) {
      console.error('Error loading caregiver data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save current form data to Nexus
  const saveToNexus = useCallback(
    async (data?: Partial<CaregiverFormData>) => {
      setIsSaving(true);
      try {
        const payload = data || formData;
        const response = await authFetch('/api/caregiver', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          if (await handleCaregiverResolutionResponse(response)) {
            return false;
          }
          throw new Error('Save failed');
        }
        return true;
      } catch (error) {
        console.error('Error saving caregiver data:', error);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, accessToken, handleCaregiverResolutionResponse]
  );

  // Fields to clear when a parent field changes
  const CASCADING_CLEARS: Record<string, string[]> = {
    citizenStatus: [
      'uscisNumber', 'noncitizenDocumentType', 'noncitizenExpirationDate',
      'formI94AdmissionNumber', 'foreignPassportNumber', 'foreignPassportCountry',
    ],
    noncitizenDocumentType: [
      'uscisNumber', 'formI94AdmissionNumber', 'foreignPassportNumber', 'foreignPassportCountry',
    ],
    hasReceivedFluVaccineThisSeason: [
      'fluVaccineDate', 'fluVaccineDecision', 'fluDeclinationReason', 'fluDeclinationOtherReason',
    ],
    sysHepBDecision: [
      'hepBImmuneStatus', 'hepBInjection1', 'hepBInjection2', 'hepBInjection3', 'hepBVaccinationDecision',
    ],
    ableToDrive: ['hasACar'],
    fluVaccineDecision: ['fluDeclinationReason', 'fluDeclinationOtherReason'],
    hepBImmuneStatus: ['hepBInjection1', 'hepBInjection2', 'hepBInjection3'],
  };

  const HEP_B_DECISION_MAP: Record<string, string> = {
    'I accept': 'I accept',
    'I decline the Hepatitis B vaccine and hold harmless the agency': 'I decline - Hold Harmless',
    'I decline - I am already immune': 'I decline - Immune',
  };

  // Handle text/select field changes with cascading clears and value derivation
  const handleChange = useCallback(
    (e: { target: { name: string; value: any } }) => {
      const { name, value } = e.target;

      setFormData((prev) => {
        const updates: Partial<CaregiverFormData> = { [name]: value };

        // Cascading clears
        const fieldsToClear = CASCADING_CLEARS[name];
        if (fieldsToClear) {
          for (const field of fieldsToClear) {
            const defaultVal = (INITIAL_FORM_DATA as any)[field];
            (updates as any)[field] = typeof defaultVal === 'boolean' ? false : '';
          }
        }

        // Derive hepBVaccinationDecision from sysHepBDecision
        if (name === 'sysHepBDecision') {
          updates.hepBVaccinationDecision = HEP_B_DECISION_MAP[value] || '';
        }

        // Auto-set fluVaccineDecision when vaccine received
        if (name === 'hasReceivedFluVaccineThisSeason' && value === 'Yes') {
          updates.fluVaccineDecision = 'I agree';
        }

        return { ...prev, ...updates };
      });

      // Clear field error on change
      setFieldErrors((prev) => {
        if (prev[name]) {
          const next = { ...prev };
          delete next[name];
          return next;
        }
        return prev;
      });
    },
    []
  );

  // Handle checkbox/boolean changes with cascading logic
  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;
      setFormData((prev) => {
        const updates: Partial<CaregiverFormData> = { [name]: checked } as any;

        // When unchecking physical address different, copy mailing to physical
        if (name === 'physicalAddressDifferentThanMailing' && !checked) {
          updates.otherAddressLine1 = prev.street;
          updates.otherAptUnitNumber = prev.aptUnitNumber;
          updates.otherCity = prev.city;
          updates.otherState = prev.state;
          updates.otherZipCode = prev.zipCode;
        }

        return { ...prev, ...updates };
      });
    },
    []
  );

  // Handle multi-select changes (array → semicolon string)
  const handleMultiSelectChange = useCallback(
    (e: { target: { name: string; value: string[] } }) => {
      const { name, value } = e.target;
      const joined = value.join(';');
      setFormData((prev) => ({ ...prev, [name]: joined }));
      setFieldErrors((prev) => {
        if (prev[name]) {
          const next = { ...prev };
          delete next[name];
          return next;
        }
        return prev;
      });
    },
    []
  );

  // Navigate to next step
  const goToNextStep = useCallback(async () => {
    const errors = validateStep(currentStep, formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }
    setFieldErrors({});

    // Save step data to Nexus with portal status
    const nextStatus = STEP_PORTAL_STATUS[currentStep] || STEP_PORTAL_STATUS[currentStep - 1];
    const saved = await saveToNexus({ ...formData, portalStatus: nextStatus });
    if (!saved) return false;

    setFormData((prev) => ({ ...prev, portalStatus: nextStatus }));
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
    return true;
  }, [currentStep, formData, saveToNexus]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    setFieldErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  // Go to a specific step (for nav bar clicks)
  const goToStep = useCallback(
    (step: number) => {
      if (step >= 1 && step < currentStep) {
        setFieldErrors({});
        setCurrentStep(step);
      }
    },
    [currentStep]
  );

  // Upload signature canvas data to blob storage, returns true on success
  const uploadSignatureBlob = useCallback(
    async (base64DataUrl: string): Promise<boolean> => {
      try {
        const parts = base64DataUrl.split(',');
        const bstr = atob(parts[1]);
        const u8arr = new Uint8Array(bstr.length);
        for (let i = 0; i < bstr.length; i++) {
          u8arr[i] = bstr.charCodeAt(i);
        }
        const blob = new Blob([u8arr], { type: 'image/png' });

        const fd = new FormData();
        fd.append('signature', blob, 'signature.png');

        const response = await authFetch('/api/caregiver/signature', {
          method: 'POST',
          body: fd,
        });
        return response.ok;
      } catch (error) {
        console.error('Error uploading signature:', error);
        return false;
      }
    },
    [accessToken]
  );

  // Final submit (signature step → submitted)
  const submitForm = useCallback(async () => {
    const errors = validateStep(SIGNATURE_STEP, formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }
    setFieldErrors({});

    // Upload signature to blob storage
    if (formData.portalSignature) {
      const uploaded = await uploadSignatureBlob(formData.portalSignature);
      if (!uploaded) return false;
    }

    const now = new Date().toISOString().split('T')[0];
    const {
      portalSignature: _localOnly,
      portalSignatureBlobKey: _blobKeyHandledByUpload,
      ...formWithoutSignatureFields
    } = formData;
    const submitData = {
      ...formWithoutSignatureFields,
      portalStatus: 'Submitted',
      portalSignatureDate: now,
    };
    const saved = await saveToNexus(submitData);
    if (!saved) return false;

    setFormData((prev) => ({
      ...prev,
      portalSignature: '',
      portalSignatureBlobKey: 'uploaded',
      portalStatus: 'Submitted',
      portalSignatureDate: now,
    }));
    setIsSubmitted(true);
    setCurrentStep(TOTAL_STEPS);
    // Unlocks the hub and availability page in the nav
    refreshPortalState();

    // Trigger document generation in background
    triggerDocGeneration();

    return true;
  }, [formData, saveToNexus, uploadSignatureBlob, refreshPortalState]);

  const triggerDocGeneration = useCallback(async () => {
    setIsGeneratingDocs(true);
    setDocGenError(null);
    try {
      const response = await authFetch('/api/documents/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Generation failed' }));
        setDocGenError(err.message);
      }
      await fetchMyDocuments();
    } catch (error) {
      console.error('Error generating documents:', error);
      setDocGenError('Failed to generate documents');
    } finally {
      setIsGeneratingDocs(false);
    }
  }, [accessToken]);

  const fetchMyDocuments = useCallback(async () => {
    try {
      const response = await authFetch('/api/documents/my?initial=true');
      if (response.ok) {
        const docs = await response.json();
        setGeneratedDocs(docs);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [accessToken]);

  return {
    formData,
    setFormData,
    fieldErrors,
    setFieldErrors,
    currentStep,
    isLoading,
    isSaving,
    isSubmitted,
    isGeneratingDocs,
    generatedDocs,
    docGenError,
    handleChange,
    handleCheckboxChange,
    handleMultiSelectChange,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    submitForm,
    saveToNexus,
    fetchMyDocuments,
  };
}
